import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { Person as PersonIcon, History as HistoryIcon } from '@mui/icons-material';
import { assignmentsService } from '../../services/assignments.service';
import type { Lead } from '../../types/lead.types';
import type { AssignmentHistory } from '../../types/assignments.types';

interface LeadAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  onAssignmentComplete?: () => void;
}

interface OrganizationUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

const LeadAssignmentModal: React.FC<LeadAssignmentModalProps> = ({
  open,
  onClose,
  lead,
  onAssignmentComplete
}) => {
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [history, setHistory] = useState<AssignmentHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // Load organization users
  useEffect(() => {
    if (open) {
      loadUsers();
      loadHistory();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      const orgUsers = await assignmentsService.getOrganizationUsers();
      setUsers(orgUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError('Failed to load organization users');
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const assignmentHistory = await assignmentsService.getLeadAssignmentHistory(lead.id);
      setHistory(assignmentHistory);
    } catch (err: any) {
      console.error('Error loading assignment history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      setError('Please select a user to assign');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await assignmentsService.assignLead({
        lead_id: lead.id,
        assigned_to: selectedUserId,
        notes: notes || undefined
      });

      // Refresh history
      await loadHistory();

      // Call callback if provided
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }

      // Reset form
      setSelectedUserId('');
      setNotes('');
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err: any) {
      console.error('Error assigning lead:', err);
      setError(err.response?.data?.message || 'Failed to assign lead');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    setLoading(true);
    setError(null);

    try {
      await assignmentsService.unassignLead(lead.id);

      // Refresh history
      await loadHistory();

      // Call callback if provided
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err: any) {
      console.error('Error unassigning lead:', err);
      setError(err.response?.data?.message || 'Failed to unassign lead');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'org_admin':
        return 'error';
      case 'sales_manager':
        return 'warning';
      case 'sales_rep':
        return 'info';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6">Assign Lead</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Lead Information */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Lead Information
          </Typography>
          <Typography variant="h6">{lead.company || 'No company'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {lead.first_name} {lead.last_name} • {lead.email}
          </Typography>
          {lead.assigned_to && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={`Currently assigned`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        {/* Assignment Form */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Assign To</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Assign To"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Select a user</em>
              </MenuItem>
              {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="body2">{user.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  <Chip
                    label={user.role.replace('_', ' ').toUpperCase()}
                    size="small"
                    color={getRoleBadgeColor(user.role)}
                  />
                </Box>
              </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this assignment..."
            disabled={loading}
          />
        </Box>

        {/* Assignment History */}
        <Box>
          <Button
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory(!showHistory)}
            sx={{ mb: 2 }}
          >
            {showHistory ? 'Hide' : 'Show'} Assignment History
          </Button>

          {showHistory && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              {loadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  No assignment history
                </Typography>
              ) : (
                <Box>
                  {history.map((item, index) => (
                    <Box key={item.id}>
                      {index > 0 && <Divider sx={{ my: 1.5 }} />}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {item.action === 'assigned' ? 'Assigned to' : 'Unassigned from'}{' '}
                              {item.assigned_to_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              by {item.assigned_by_name} • {formatDate(item.created_at)}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.action}
                            size="small"
                            color={item.action === 'assigned' ? 'success' : 'default'}
                          />
                        </Box>
                        {item.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Note: {item.notes}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {lead.assigned_to && (
          <Button
            onClick={handleUnassign}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Unassign
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          disabled={loading || !selectedUserId}
          variant="contained"
          color="primary"
        >
          {loading ? <CircularProgress size={24} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadAssignmentModal;
